import { BrowserModule } from '@angular/platform-browser';
import {
  APP_INITIALIZER,
  CUSTOM_ELEMENTS_SCHEMA,
  Injector,
  NgModule,
} from '@angular/core';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LoginPageComponent } from './pages/login-page/login-page.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SessionStorageService } from './core/services/storage/session-storage.service';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { InterceptorService } from './core/services/interceptor/interceptor.service';
import { GeneralHttpService } from './core/services/general-http.service';
import { AuthService } from './core/services/AuthService/auth.service';
import { AuthGuard } from './core/services/auth.guard';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppMaterialModule } from './app-material/app-material.module';
import { LogoComponent } from './core/components/logo/logo.component';
import { LogoModule } from './core/components/logo/logo.module';
import { DashboardComponent } from './pages/dashboard/dashboard.component';
import {
  AccumulationChartAllModule,
  ChartAllModule,
  ChartModule,
  RangeNavigatorAllModule,
} from '@syncfusion/ej2-angular-charts';
import { SidenavComponent } from './core/components/sidenav/sidenav.component';
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import {
  DropDownListModule,
  MultiSelectModule,
} from '@syncfusion/ej2-angular-dropdowns';
import { SideNavModule } from './core/components/sidenav/sidenav.module';
import {
  DatePipe,
  HashLocationStrategy,
  LocationStrategy,
} from '@angular/common';
import { BarSpinnerButtonComponent } from './core/components/bar-spinner-button/bar-spinner-button.component';
import { ConfirmDialogComponent } from './core/components/modals/confirm-dialog/confirm-dialog.component';
import { AlertDialogComponent } from './core/components/modals/alert-dialog/alert-dialog.component';
import { ConfirmDialogService } from './core/components/modals/confirm-dialog.service';
import { AlertDialogService } from './core/components/modals/alert-dialog.service';
import { WhiteLabelJsonService } from './core/services/white-label-json.service';
import { PartnerListPageComponent } from './pages/partner-list-page/partner-list-page.component';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { UserRightsService } from './core/services/user-rights.service';
import {
  AuthApiGeneratedService,
  AUTH_API_BASE_URL,
} from './core/services/AuthService/auth-service-generate';
import {
  AdminGeneratedService,
  ADMIN_API_BASE_URL,
} from './core/services/AdminService/admin-service-generate';
import { environment } from 'src/environments/environment';
import { PartnerCreateUpdateComponent } from './pages/partner-create-update/partner-create-update.component';
import { GridFooterInfoComponent } from './core/components/grid-footer-info/grid-footer-info.component';
import { GridFooterInfoModule } from './core/components/grid-footer-info/grid-footer-info.module';
import { ToastService } from './core/services/toast.service';
import { RoleRightGuard } from './core/services/role-right.guard';
import { NgxSpinnerModule } from 'ngx-spinner';
import { GridLocalizationService } from './core/services/grid-localization.service';
import { GridLocaleStorageClearService } from './core/services/grid-locale-storage-clear.service';
import { ServiceWithCacheService } from './core/cachableServices/service-with-cache.service';
import { ErrorHandler } from '@angular/core';
import { GlobalErrorHandler } from './core/services/global-error-handler';
import { OperatorPermissionDialogComponent } from './core/components/modals/operator-permission-dialog/operator-permission-dialog.component';
import { CustomerBulkExcelStructureDialogComponent } from './core/components/modals/customer-bulk-excel-structure-dialog/customer-bulk-excel-structure-dialog.component';
import { SpreadsheetAllModule } from '@syncfusion/ej2-angular-spreadsheet';
import { UploaderModule } from '@syncfusion/ej2-angular-inputs';
import {
  EditService,
  GridAllModule,
  ToolbarService,
} from '@syncfusion/ej2-angular-grids';
import { ToolbarModule } from '@syncfusion/ej2-angular-navigations';
import { DialogModule } from '@syncfusion/ej2-angular-popups';
import { ContourHistoryDialogComponent } from './core/components/modals/contour-history-dialog/contour-history-dialog.component';
import { appInitializerFactory } from './core/services/appInitializer';
import { DocumentDownloadService } from './core/services/DocumentDownloadService/document-download.service';
import { AllServicesMonthlyDocumentCountDialogComponent } from './core/components/modals/all-services-monthly-document-count-dialog/all-services-monthly-document-count-dialog.component';
import { FastTransactionDialogComponent } from './core/components/modals/fast-transaction-dialog/fast-transaction-dialog.component';
import { DocumentHtmlPipe } from './core/custom-pipe/document-html.pipe';
import { RuleDetailsComponent } from './core/components/modals/rule-details/rule-details.component';
import { ChangePartnerDialogComponent } from './core/components/modals/change-partner-dialog/change-partner-dialog.component';
import { ChangeCustomerDialogComponent } from './core/components/modals/change-customer-dialog/change-customer-dialog.component';
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

@NgModule({
  declarations: [
    AppComponent,
    ConfirmDialogComponent,
    DocumentHtmlPipe,
    AlertDialogComponent,
    OperatorPermissionDialogComponent,
    CustomerBulkExcelStructureDialogComponent,
    ContourHistoryDialogComponent,
    AllServicesMonthlyDocumentCountDialogComponent,
    FastTransactionDialogComponent,
    RuleDetailsComponent,
    ChangePartnerDialogComponent,
    ChangeCustomerDialogComponent,
  ],
  imports: [
    ReactiveFormsModule,
    SideNavModule,
    BrowserModule,
    SpreadsheetAllModule,
    UploaderModule,
    GridAllModule,
    AppRoutingModule,
    ToolbarModule,
    DialogModule,
    FormsModule,
    // LogoModule,
    // GridFooterInfoModule,
    HttpClientModule,
    BrowserAnimationsModule,
    AppMaterialModule,
    DropDownListModule,
    MultiSelectModule,
    NgxSpinnerModule,
    ChartAllModule,
    ChartModule,
    AccumulationChartAllModule,
    RangeNavigatorAllModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  // tslint:disable-next-line: max-line-length
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [TranslateService, Injector],
      multi: true,
    },
    [{ provide: LocationStrategy, useClass: HashLocationStrategy }], // bu kısım portal refresh oldugunudakı env.js e erısememe sorununu cozuyor
    SessionStorageService,
    GeneralHttpService,
    DocumentDownloadService,
    AuthApiGeneratedService,
    AdminGeneratedService,
    ServiceWithCacheService,
    DatePipe,
    GridLocalizationService,
    GridLocaleStorageClearService,
    WhiteLabelJsonService,
    UserRightsService,
    ConfirmDialogService,
    AlertDialogService,
    ToastService,
    AuthService,
    AuthGuard,
    RoleRightGuard,
    { provide: HTTP_INTERCEPTORS, useClass: InterceptorService, multi: true },
    { provide: MAT_DATE_LOCALE, useValue: 'tr-TR' },
    { provide: AUTH_API_BASE_URL, useValue: environment.authApiUrl },
    { provide: ADMIN_API_BASE_URL, useValue: environment.adminApiUrl },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
